import type { TOC } from '@ember/component/template-only';

// ---------------------------------------------------------------------------
// Card – minimal wrapper for the standard reimagined card treatment
// ---------------------------------------------------------------------------

/**
 * Padding size options for the card
 */
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

interface CardSignature {
  Args: {
    /** Padding size (default: 'md' = p-5) */
    padding?: CardPadding;
    /**
     * Compact cards take rounded-lg; the default is the card radius,
     * rounded-2xl per DESIGN.md's radius table.
     */
    compact?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

const paddingClasses: Record<CardPadding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
};

const getPadding = (padding: CardPadding = 'md') => paddingClasses[padding];

/**
 * Card - A minimal wrapper for the standard reimagined card treatment
 *
 * Replaces the repeated `tio-bg-surface rounded-* border
 * tio-border-default p-X` class string found in 20+ places.
 *
 * @example Basic usage
 * ```gts
 * import Card from 'tio-ui/components/card';
 *
 * <Card>
 *   <p>Card content</p>
 * </Card>
 * ```
 *
 * @example With large padding
 * ```gts
 * <Card @padding="lg">
 *   <p>Card content with more padding</p>
 * </Card>
 * ```
 */
const Card: TOC<CardSignature> = <template>
  <div
    data-test-card
    class="tio-bg-surface
      {{if @compact 'rounded-lg' 'rounded-2xl'}}
      border tio-border-default
      {{getPadding @padding}}"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

export default Card;

export type { CardSignature };
